import { useCallback, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Check, Cross, Upload } from '@factorialco/f0-react/icons/app'
import {
  ApproveTrainingRequestDatatableDocument,
  TrainingRequestsDataCollectionDocument,
  TrainingRequestsDataCollectionFragmentFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import asyncAction from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import apiClient from 'modules/core/lib/mobx-rest/apiClient'
import app from 'modules/core/store/models/app'
import { EXPORT_FORMATS } from 'modules/core/components/DataTable/constants'

interface UseActionsParams {
  onReject: (id: string) => void
}

export const useActions = ({ onReject }: UseActionsParams) => {
  const apolloClient = useApolloClient()
  const {
    company: { id: companyId },
    access: { id: currentAccessId },
  } = useCurrentSession()

  const [approve] = useMutation(ApproveTrainingRequestDatatableDocument, {
    extractorFn: (data) => data.trainingsMutations.approveTrainingsTrainingRequest,
    refetchQueries: [TrainingRequestsDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  const onApprove = useCallback(
    async (id: string) => {
      const result = await approve({
        variables: { id, companyId },
      })

      const handled = handleResult(result, () => {
        app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
      })

      if (handled.success) {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({ __typename: 'TrainingsTrainingRequest', id }),
          fields: {
            status: () => 'approved',
          },
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.training_requests.create.success_messages.approved'),
        })
      }
    },
    [approve, apolloClient, companyId]
  )

  const itemActions = useCallback(
    (item: TrainingRequestsDataCollectionFragmentFragment) => {
      const currentApproverIds = compact(item.currentApproverIds)
      const isApprover = currentApproverIds.includes(currentAccessId)
      const showApproveOptions = item.status === 'review' && isApprover

      if (!showApproveOptions) return []

      return [
        {
          label: i18n.t('common.approve'),
          icon: Check,
          type: 'primary' as const,
          onClick: () => onApprove(item.id),
        },
        {
          label: i18n.t('common.reject'),
          icon: Cross,
          type: 'primary' as const,
          onClick: () => onReject(item.id),
        },
      ]
    },
    [currentAccessId, onApprove, onReject]
  )

  return {
    onApprove,
    itemActions,
  }
}

export const useExportAction = () => {
  const [isExporting, setIsExporting] = useState(false)

  const mappedVariablesRef = useRef<{
    search?: string
    filter?: Record<string, unknown>
    sortOrder?: { field: string; order: string }
  }>({})

  const onExport = useCallback(() => {
    if (isExporting) return

    const action = async () => {
      setIsExporting(true)

      try {
        const fileName = `${new Date().toISOString().slice(0, 10)}-${i18n.t(
          'trainings.requests_export_name'
        )}`

        await apiClient().post('/data_table/exports', {
          query: `
            query TrainingRequestsDatatable($search: String, $filter: TrainingsTrainingRequestFilter, $sortOrder: Sort) {
              trainings {
                trainingRequestsConnection(search: $search, filter: $filter, sortOrder: $sortOrder) @dataSource {
                  edges {
                    node {
                      id
                      trainingName
                      createdAt
                      author { id fullName }
                      participants { id fullName }
                      status
                      cost
                    }
                  }
                  totalCount
                }
              }
            }
          `,
          variables: {
            search: mappedVariablesRef.current.search,
            filter: mappedVariablesRef.current.filter,
            sortOrder: mappedVariablesRef.current.sortOrder,
          },
          projections: [
            {
              path: 'trainingName',
              title: i18n.t(
                'trainings.training_requests.my_requests.datatable.columns.training_name'
              ),
            },
            {
              path: 'createdAt',
              title: i18n.t(
                'trainings.training_requests.my_requests.datatable.columns.request_date'
              ),
            },
            {
              path: 'author.fullName',
              title: i18n.t('trainings.training_requests.requests.datatable.requested_by'),
            },
            {
              path: 'status',
              title: i18n.t('trainings.training_requests.requests.datatable.status'),
            },
            { path: 'cost', title: i18n.t('trainings.training_requests.create.cost') },
          ],
          fileFormat: EXPORT_FORMATS.EXCEL,
          fileName,
        })
      } finally {
        setIsExporting(false)
      }
    }

    asyncAction(action, {
      successMessage: i18n.t('data_table.export.success'),
    })
  }, [isExporting])

  const secondaryActions = {
    expanded: 1 as const,
    actions: () => [
      {
        label: i18n.t('common.export'),
        icon: Upload,
        onClick: onExport,
        disabled: isExporting,
      },
    ],
  }

  return { secondaryActions, mappedVariablesRef }
}
